import React, { useState, useEffect, useRef } from "react";
import {
  Calendar as CalendarIcon,
  Megaphone,
  Plus,
  Edit,
  Trash2,
  X,
  Eye,
  Download,
  Search,
  Users,
  Star,
} from "lucide-react";

/**
 * Events & Announcements — Right-side Drawer Edition (Error-free version)
 * - Removed duplicate setItems issue
 * - Cleaner state handling
 */

export default function EventsAnnouncements() {
  const [items, setItems] = useState(() => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    const dayAfter = new Date(now);
    dayAfter.setDate(now.getDate() + 3);

    return [
      {
        id: 1,
        type: "event",
        title: "Quarterly Townhall",
        description: "All-hands meeting to share Q3 product updates and roadmap.",
        date: isoDate(now),
        time: "15:00",
        location: "Main Auditorium",
        published: true,
        featured: true,
        attendees: 125,
        image: null,
      },
      {
        id: 2,
        type: "announcement",
        title: "Policy Update: Remote Work",
        description: "Remote work policy updated. Please review the HR portal for details.",
        date: isoDate(tomorrow),
        time: "09:00",
        location: "",
        published: false,
        featured: false,
        attendees: 0,
        image: null,
      },
      {
        id: 3,
        type: "event",
        title: "Team Offsite — Engineering",
        description: "One-day offsite with team-building activities.",
        date: isoDate(dayAfter),
        time: "10:00",
        location: "Blue Lake Resort",
        published: true,
        featured: false,
        attendees: 34,
        image: null,
      },
    ];
  });

  const [filterType, setFilterType] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [previewImage, setPreviewImage] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    setPreviewImage(editing?.image || null);
  }, [editing]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && drawerOpen) closeDrawer();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  function isoDate(d) {
    const dt = new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  function emptyForm() {
    return {
      type: "event",
      title: "",
      description: "",
      date: isoDate(new Date()),
      time: "09:00",
      location: "",
      published: false,
      featured: false,
      attendees: 0,
      imageDataUrl: null,
    };
  }

  function openCreate() {
    setEditing(null);
    setForm(emptyForm());
    setPreviewImage(null);
    if (fileRef.current) fileRef.current.value = "";
    setDrawerOpen(true);
  }

  function openEdit(item) {
    setEditing(item);
    setForm({
      type: item.type,
      title: item.title,
      description: item.description,
      date: item.date,
      time: item.time,
      location: item.location,
      published: item.published,
      featured: item.featured,
      attendees: item.attendees,
      imageDataUrl: item.image || null,
    });
    setPreviewImage(item.image || null);
    if (fileRef.current) fileRef.current.value = "";
    setDrawerOpen(true);
  }

  function closeDrawer() {
    setDrawerOpen(false);
    setEditing(null);
    setForm(emptyForm());
    setPreviewImage(null);
    if (fileRef.current) fileRef.current.value = "";
  }

  function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      setForm((f) => ({ ...f, imageDataUrl: ev.target.result }));
      setPreviewImage(ev.target.result);
    };
    reader.readAsDataURL(file);
  }

  function saveForm(e) {
    e.preventDefault();
    if (!form.title.trim()) return alert("Please add a title.");

    if (editing) {
      setItems((prev) =>
        prev.map((it) =>
          it.id === editing.id
            ? {
                ...it,
                ...form,
                attendees: Number(form.attendees || 0),
                image: form.imageDataUrl,
              }
            : it
        )
      );
    } else {
      setItems((prev) => [
        {
          id: Date.now(),
          ...form,
          attendees: Number(form.attendees || 0),
          image: form.imageDataUrl,
        },
        ...prev,
      ]);
    }

    closeDrawer();
  }

  function removeItem(id) {
    if (!window.confirm("Delete this item?")) return;
    setItems((prev) => prev.filter((i) => i.id !== id));
  }

  function togglePublish(id) {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, published: !it.published } : it)));
  }

  function toggleFeatured(id) {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, featured: !it.featured } : it)));
  }

  function addRSVP(id) {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, attendees: it.attendees + 1 } : it)));
  }

  function exportICS(item) {
    const dt = new Date(`${item.date}T${item.time}:00`);
    const dtEnd = new Date(dt.getTime() + 3600000);
    const pad = (v) => String(v).padStart(2, "0");
    const fmt = (d) =>
      `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(
        d.getUTCHours()
      )}${pad(d.getUTCMinutes())}00Z`;

    const text = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "BEGIN:VEVENT",
      `UID:${item.id}@example.com`,
      `DTSTAMP:${fmt(new Date())}`,
      `DTSTART:${fmt(dt)}`,
      `DTEND:${fmt(dtEnd)}`,
      `SUMMARY:${item.title}`,
      `DESCRIPTION:${item.description}`,
      `LOCATION:${item.location}`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");

    const blob = new Blob([text], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${item.title.replace(/\s+/g, "_")}.ics`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // FILTERED LIST
  const filtered = items.filter((i) => {
    const matchesType = filterType === "all" || i.type === filterType;
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      !term ||
      i.title.toLowerCase().includes(term) ||
      i.description.toLowerCase().includes(term);
    return matchesType && matchesSearch;
  });

  const upcoming = items
    .filter((i) => i.type === "event")
    .sort((a, b) => new Date(`${a.date}T${a.time}`) - new Date(`${b.date}T${b.time}`))
    .slice(0, 6);

  // ---------- RENDER ----------
  return (
    <div className="min-h-screen bg-gray-50 p-6 md:p-8 font-['Poppins','Roboto',sans-serif]">
      <div className="max-w-7xl mx-auto">
        {/* ---------------- Header ---------------- */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2 text-gray-800">
              <Megaphone className="text-teal-600" /> Events & Announcements
            </h1>
            <p className="text-gray-500 mt-1">Create events & announcements.</p>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center bg-white border rounded-lg px-3 py-2 shadow">
              <Search size={16} className="text-gray-400 mr-2" />
              <input
                placeholder="Search..."
                className="outline-none w-64"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <select
              className="border rounded-lg p-2 bg-white"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="all">All</option>
              <option value="event">Events</option>
              <option value="announcement">Announcements</option>
            </select>

            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg shadow hover:bg-teal-700"
            >
              <Plus size={16} /> New
            </button>
          </div>
        </div>

        {/* ---------------- Main Grid ---------------- */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* List */}
          <div className="lg:col-span-3 space-y-4">
            {filtered.length === 0 ? (
              <div className="bg-white p-6 text-center rounded shadow text-gray-500">
                No items found.
              </div>
            ) : (
              filtered.map((it) => (
                <div key={it.id} className="bg-white p-4 rounded-lg shadow flex gap-4">
                  {/* icon/image */}
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center">
                    {it.image ? (
                      <img src={it.image} className="w-full h-full object-cover" alt="" />
                    ) : (
                      <CalendarIcon className="text-teal-600" size={32} />
                    )}
                  </div>

                  {/* content */}
                  <div className="flex-1">
                    <div className="flex justify-between">
                      <h3 className="text-lg font-semibold text-gray-800">{it.title}</h3>
                      <span className="text-sm text-gray-500">
                        {it.date} • {formatTime(it.time)}
                      </span>
                    </div>

                    <p className="text-sm text-gray-600 mt-1">{it.description}</p>

                    <div className="flex gap-3 mt-3">
                      <button onClick={() => openEdit(it)} className="px-3 py-1 border rounded text-indigo-600 flex items-center gap-1 text-sm">
                        <Edit size={14} /> Edit
                      </button>
                      <button onClick={() => removeItem(it.id)} className="px-3 py-1 border rounded text-red-600 flex items-center gap-1 text-sm">
                        <Trash2 size={14} /> Delete
                      </button>
                      <button onClick={() => togglePublish(it.id)} className="px-3 py-1 border rounded text-sm">
                        {it.published ? "Unpublish" : "Publish"}
                      </button>
                      <button onClick={() => addRSVP(it.id)} className="px-3 py-1 border rounded text-sm flex items-center gap-2">
                        <Users size={14} /> RSVP
                      </button>
                      <button onClick={() => exportICS(it)} className="px-3 py-1 border rounded text-sm flex items-center gap-2">
                        <Download size={14} /> ICS
                      </button>
                      <button onClick={() => toggleFeatured(it.id)} className="px-3 py-1 border rounded text-sm flex items-center gap-2">
                        <Star size={14} /> {it.featured ? "Unfeature" : "Feature"}
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-4">
            <div className="bg-white p-4 rounded-lg shadow">
              <h4 className="font-semibold flex items-center gap-2">
                <CalendarIcon className="text-teal-600" /> Upcoming
              </h4>
              <ul className="mt-3 space-y-3 text-sm">
                {upcoming.map((u) => (
                  <li key={u.id} className="flex justify-between">
                    <div>
                      <div className="text-gray-700 font-medium">{u.title}</div>
                      <div className="text-gray-500">{u.date}</div>
                    </div>
                    <div className="text-teal-600">{u.attendees}</div>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>

      {/* Drawer */}
      <Drawer open={drawerOpen} onClose={closeDrawer} title={editing ? "Edit Item" : "New Item"}>
        <form onSubmit={saveForm} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* TYPE */}
            <div>
              <label className="text-sm text-gray-600">Type</label>
              <select
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                className="w-full border rounded p-2 mt-1"
              >
                <option value="event">Event</option>
                <option value="announcement">Announcement</option>
              </select>
            </div>

            {/* TITLE */}
            <div>
              <label className="text-sm text-gray-600">Title</label>
              <input
                value={form.title}
                required
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                className="w-full border rounded p-2 mt-1"
              />
            </div>

            {/* DESC */}
            <div className="md:col-span-2">
              <label className="text-sm text-gray-600">Description</label>
              <textarea
                rows={4}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                className="w-full border rounded p-2 mt-1"
              ></textarea>
            </div>

            {/* DATE */}
            <div>
              <label className="text-sm text-gray-600">Date</label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                className="w-full border rounded p-2 mt-1"
              />
            </div>

            {/* TIME */}
            <div>
              <label className="text-sm text-gray-600">Time</label>
              <input
                type="time"
                value={form.time}
                onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}
                className="w-full border rounded p-2 mt-1"
              />
            </div>

            {/* LOCATION */}
            <div>
              <label className="text-sm text-gray-600">Location</label>
              <input
                value={form.location}
                onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                className="w-full border rounded p-2 mt-1"
              />
            </div>

            {/* ATTENDEES */}
            <div>
              <label className="text-sm text-gray-600">Attendees</label>
              <input
                type="number"
                value={form.attendees}
                onChange={(e) => setForm((f) => ({ ...f, attendees: e.target.value }))}
                className="w-full border rounded p-2 mt-1"
              />
            </div>

            {/* IMAGE */}
            <div className="md:col-span-2">
              <label className="text-sm text-gray-600">Image</label>
              <div className="flex items-center gap-2 mt-1">
                <input ref={fileRef} type="file" accept="image/*" onChange={handleFileChange} />
                {previewImage && (
                  <img src={previewImage} className="w-20 h-14 object-cover rounded border" alt="" />
                )}
              </div>
            </div>

            {/* PUBLISHED */}
            <div>
              <label className="text-sm text-gray-600">Published</label>
              <select
                value={form.published}
                onChange={(e) => setForm((f) => ({ ...f, published: e.target.value === "true" }))}
                className="w-full border rounded p-2 mt-1"
              >
                <option value="false">Draft</option>
                <option value="true">Published</option>
              </select>
            </div>

            {/* FEATURED */}
            <div>
              <label className="text-sm text-gray-600">Featured</label>
              <select
                value={form.featured}
                onChange={(e) => setForm((f) => ({ ...f, featured: e.target.value === "true" }))}
                className="w-full border rounded p-2 mt-1"
              >
                <option value="false">No</option>
                <option value="true">Yes</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={closeDrawer}
              className="px-4 py-2 bg-gray-200 rounded"
            >
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 bg-teal-600 text-white rounded">
              {editing ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </Drawer>
    </div>
  );
}

/* Drawer Component */
function Drawer({ open, onClose, title, children }) {
  return (
    <>
      {/* backdrop */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-40 z-40 transition-opacity ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* drawer */}
      <aside
        className={`fixed top-0 right-0 bg-white w-full md:w-3/5 lg:w-2/5 xl:w-1/3 h-full z-50 shadow-2xl transform transition-transform ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold">{title}</h3>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded">
              <X size={20} />
            </button>
          </div>

          <div className="p-6 overflow-y-auto" style={{ maxHeight: "calc(100vh - 60px)" }}>
            {children}
          </div>
        </div>
      </aside>
    </>
  );
}

// Time format helper
function formatTime(t) {
  const [h, m] = t.split(":");
  const hour = Number(h);
  const ampm = hour >= 12 ? "PM" : "AM";
  const hr = hour % 12 || 12;
  return `${String(hr).padStart(2, "0")}:${m} ${ampm}`;
}
